import { useEffect, useMemo, useState } from "react";
import { CaretUp, CaretDown, MapPin, Trash, BatteryHigh, Crosshair, SignOut, CheckCircle, Bell, DownloadSimple, PersonSimpleWalk, Question, ShareNetwork, PencilSimple } from "@phosphor-icons/react";
import { haversine, fmtDistance, fmtWalk, freshnessTier, fmtAccuracy } from "@/lib/geo";

export default function ControlSheet({
  session, members, myPos, meet, batterySaver, setBatterySaver,
  placing, onStartPlace, onCancelPlace, onClearMeet, onRecenter, onLeave,
  onPingFriend, onSaveOffline, onOpenHelp, onShare, onOpenPinPicker,
}) {
  const [open, setOpen] = useState(true);

  const friends = useMemo(() => {
    return members
      .filter((m) => m.user_id !== session.userId)
      .map((m) => ({ ...m, dist: myPos ? haversine(myPos, { lat: m.lat, lng: m.lng }) : null }))
      .sort((a, b) => (a.dist ?? 1e12) - (b.dist ?? 1e12));
  }, [members, myPos, session.userId]);

  const isCreator = meet && meet.created_by === session.userId;
  const acceptedCount = meet?.responses
    ? Object.values(meet.responses).filter((r) => r.response === "accept").length
    : 0;
  const meetEta = meet && meet.lat != null && myPos
    ? fmtWalk(haversine(myPos, { lat: meet.lat, lng: meet.lng }))
    : null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[50] flex flex-col items-center px-3 pb-3">
      <div className="pointer-events-auto w-full max-w-md border-2 border-black bg-white shadow-[6px_6px_0px_rgba(0,0,0,1)]">
        {/* header row */}
        <div className="flex items-stretch border-b-2 border-black bg-secondary text-white">
          <button
            data-testid="sheet-toggle"
            onClick={() => setOpen((o) => !o)}
            className="flex flex-1 items-center justify-between px-4 py-3"
          >
            <span className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide">
              <MapPin weight="fill" size={18} className="text-primary" />
              {session.groupCode} · {members.length} online
            </span>
            {open ? <CaretDown weight="bold" size={20} /> : <CaretUp weight="bold" size={20} />}
          </button>
          <button
            data-testid="open-help-button"
            onClick={onOpenHelp}
            title="How it works"
            className="grid w-12 place-items-center border-l-2 border-black bg-primary text-black"
          >
            <Question weight="bold" size={20} />
          </button>
        </div>

        {open && (
          <div className="flex flex-col gap-3 p-4">
            {/* your pin */}
            <button
              data-testid="customize-pin-button"
              onClick={onOpenPinPicker}
              className="flex items-center justify-between gap-2 border-2 border-black bg-background px-3 py-2 shadow-[3px_3px_0px_rgba(0,0,0,1)] transition-transform duration-150 ease-out active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
            >
              <span className="flex items-center gap-2 overflow-hidden">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 border-black text-sm" style={{ background: session.color || "#3B82F6" }}>
                  {session.emoji}
                </span>
                <span className="truncate font-heading text-sm font-bold uppercase">{session.name} <span className="text-black/40">(you)</span></span>
              </span>
              <span className="flex shrink-0 items-center gap-1 font-mono2 text-[10px] font-bold uppercase tracking-widest text-black/50">
                <PencilSimple weight="bold" size={14} /> Pin
              </span>
            </button>
            {/* meet banner */}
            {meet && meet.lat != null && (
              <div className="flex items-center justify-between border-2 border-black bg-accent px-3 py-2 text-white shadow-[3px_3px_0px_rgba(0,0,0,1)]">
                <div className="flex items-center gap-2">
                  <CheckCircle weight="fill" size={18} />
                  <span className="font-mono2 text-xs font-bold uppercase leading-tight">
                    {meet.label || "Meet point"} · {acceptedCount} coming
                    {meetEta && (
                      <span className="mt-0.5 flex items-center gap-1 normal-case text-white/80">
                        <PersonSimpleWalk weight="bold" size={13} /> ~{meetEta}
                      </span>
                    )}
                  </span>
                </div>
                {isCreator && (
                  <button data-testid="clear-meet-button" onClick={onClearMeet} className="grid h-8 w-8 shrink-0 place-items-center border-2 border-black bg-white text-black">
                    <Trash weight="bold" size={16} />
                  </button>
                )}
              </div>
            )}

            {/* friends list */}
            <div className="max-h-44 overflow-y-auto" data-testid="friends-list">
              {friends.length === 0 && (
                <p className="py-3 text-center font-mono2 text-xs font-medium text-black/50">
                  No friends yet. Share code <b>{session.groupCode}</b> with your crew.
                </p>
              )}
              {friends.map((f) => {
                const tier = freshnessTier(f.last_updated);
                const acc = fmtAccuracy(f.accuracy);
                return (
                <div key={f.user_id} data-testid={`friend-row-${f.user_id}`} className="flex items-center justify-between gap-2 border-b border-black/10 py-2">
                  <div className="flex items-center gap-2 overflow-hidden">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 border-black text-[10px]" style={{ background: f.color || "#3B82F6" }}>
                      {f.emoji}
                    </span>
                    <div className="overflow-hidden">
                      <p className="truncate font-heading text-sm font-bold uppercase leading-none">{f.name}</p>
                      <div className="mt-0.5 flex items-center gap-1.5" data-testid={`friend-freshness-${f.user_id}`}>
                        <span className="inline-block h-2 w-2 rounded-full" style={{ background: tier.color }} />
                        <span className="font-mono2 text-[10px] font-bold" style={{ color: tier.color }}>{tier.label}</span>
                        {acc && <span className="font-mono2 text-[10px] text-black/40">· {acc}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span className="border-2 border-black bg-primary px-2 py-0.5 font-mono2 text-xs font-bold">
                      {fmtDistance(f.dist)}
                    </span>
                    <button
                      data-testid={`buzz-button-${f.user_id}`}
                      onClick={() => onPingFriend(f)}
                      title={`Buzz ${f.name}`}
                      className="grid h-8 w-8 place-items-center border-2 border-black bg-accent text-white shadow-[2px_2px_0px_rgba(0,0,0,1)] transition-transform duration-150 ease-out active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
                    >
                      <Bell weight="fill" size={15} />
                    </button>
                  </div>
                </div>
                );
              })}
            </div>

            {/* primary actions */}
            {placing ? (
              <button
                data-testid="cancel-place-button"
                onClick={onCancelPlace}
                className="flex h-14 items-center justify-center gap-2 border-2 border-black bg-white font-heading text-sm font-bold uppercase shadow-[3px_3px_0px_rgba(0,0,0,1)]"
              >
                Tap the map · Cancel
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="drop-meet-button"
                  onClick={onStartPlace}
                  className="flex h-14 items-center justify-center gap-2 border-2 border-black bg-accent font-heading text-sm font-bold uppercase text-white shadow-[3px_3px_0px_rgba(0,0,0,1)] transition-transform duration-150 ease-out active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
                >
                  <MapPin weight="fill" size={18} /> Meet Here
                </button>
                <button
                  data-testid="share-button"
                  onClick={onShare}
                  className="flex h-14 items-center justify-center gap-2 border-2 border-black bg-secondary font-heading text-sm font-bold uppercase text-white shadow-[3px_3px_0px_rgba(0,0,0,1)] transition-transform duration-150 ease-out active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
                >
                  <ShareNetwork weight="bold" size={18} /> Invite
                </button>
              </div>
            )}

            {/* utility icon row */}
            <div className="grid grid-cols-4 gap-2">
              <button data-testid="recenter-button" onClick={onRecenter} title="Center on me" className="flex flex-col items-center justify-center gap-1 border-2 border-black bg-white py-2 shadow-[2px_2px_0px_rgba(0,0,0,1)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                <Crosshair weight="bold" size={18} />
                <span className="font-mono2 text-[9px] font-bold uppercase">Me</span>
              </button>
              <button data-testid="save-offline-button" onClick={onSaveOffline} title="Save map offline" className="flex flex-col items-center justify-center gap-1 border-2 border-black bg-white py-2 shadow-[2px_2px_0px_rgba(0,0,0,1)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                <DownloadSimple weight="bold" size={18} />
                <span className="font-mono2 text-[9px] font-bold uppercase">Save Map</span>
              </button>
              <button
                data-testid="battery-switch"
                onClick={() => setBatterySaver(!batterySaver)}
                aria-pressed={batterySaver}
                title="Battery saver"
                className={`flex flex-col items-center justify-center gap-1 border-2 border-black py-2 shadow-[2px_2px_0px_rgba(0,0,0,1)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${batterySaver ? "bg-primary" : "bg-white"}`}
              >
                <BatteryHigh weight="bold" size={18} />
                <span className="font-mono2 text-[9px] font-bold uppercase">{batterySaver ? "Saver On" : "Battery"}</span>
              </button>
              <button data-testid="leave-button" onClick={onLeave} title="Leave group" className="flex flex-col items-center justify-center gap-1 border-2 border-black bg-secondary py-2 text-white shadow-[2px_2px_0px_rgba(0,0,0,1)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                <SignOut weight="bold" size={18} />
                <span className="font-mono2 text-[9px] font-bold uppercase">Leave</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
